import { IProduct, IPurchase } from '@snackpass/snackpass-types';
import { compose } from 'lodash/fp';
import moment from 'moment';
import io from 'socket.io-client';

import { API_ENDPOINT, isProd } from './config';
import {
    fetchPurchases,
    IScreen,
    setScreen,
    upsertPurchase,
    setReconnecting,
    createProduct,
    updateProduct,
    deleteProduct,
    fetchStoreMenu,
} from '../Redux';
import { ReconnectingStatus } from '../Redux/types';
import store from '../Redux/store';
import Constants from '../Utils/constants';
import { auth } from './firebase';
import { getIdToken } from 'firebase/auth';
import { getDeviceId, getDeviceSerial, getDeviceToken } from '../Utils/helpers';

const { dispatch } = store;

const _fetchPurchases = compose(dispatch, fetchPurchases);
const _fetchActiveStoreProducts = compose(dispatch, fetchStoreMenu);
const _upsertPurchase = compose(dispatch, upsertPurchase);
const _updateScreen = compose(dispatch, setScreen);
const _createProduct = compose(dispatch, createProduct);
const _updateProduct = compose(dispatch, updateProduct);
const _deleteProduct = compose(dispatch, deleteProduct);

type SocketQuery =
    | {
          deviceToken: string;
      }
    | { token: string };

/**
 * A SocketManager represents a socket.io connection to the server.
 * Please read this before using websockets. This class is based
 * off of src/lib/Websocket.ts in the @snackpass/pos repo.
 * SocketManager should only be initialized once.
 */
export default class SocketManager {
    _socket: any;
    _storeId: string | null;
    connected: boolean;
    usingDeviceToken: boolean;
    reconnectTimeout: NodeJS.Timeout | null;
    reconnectInterval: NodeJS.Timeout | null;
    shouldBackoffResync: boolean = false;
    afterReconnectTimeoutId: NodeJS.Timeout | undefined;

    constructor() {
        this._storeId = null;
        this.connected = false;
        this.usingDeviceToken = false;
        this.reconnectTimeout = null;
        this.reconnectInterval = null;

        // periodically resync purchases
        setInterval(() => {
            this.resyncPurchases();
        }, Constants.intervals.FULL_RESYNC_INTERVAL);
    }

    async getFirebaseToken() {
        if (!auth.currentUser) return;
        return await getIdToken(auth.currentUser);
    }

    set storeId(newStoreId: string) {
        if (this.connected && this._storeId) {
            this._socket.emit('store.leave', this._storeId);
        }

        this._storeId = newStoreId;

        if (this.connected) {
            this._socket.emit('store.join', this._storeId);
        }
    }

    async connect() {
        if (this._socket) return;

        const uri = `${API_ENDPOINT}/pos`;
        let query: SocketQuery;
        const deviceToken = getDeviceToken();
        const serial = getDeviceSerial();
        const deviceId = getDeviceId();
        if (deviceToken) {
            this.usingDeviceToken = true;
            query = { deviceToken };
        } else {
            const token = await this.getFirebaseToken();
            query = { token: token ?? 'not-set' };
        }
        !isProd && console.log('[websockets.ts] connect():', { uri, query });
        this._socket = io(uri, {
            transports: ['websocket'],
            secure: true,
            query,
        });

        this._socket.on('error', (err: any) => {
            console.log(`[websockets.ts] error: ${err}`);
        });

        this._socket.on('connect', () => {
            !isProd &&
                console.log('[websockets.ts] connected:', { uri, query });
            this.connected = true;
            dispatch(setReconnecting(ReconnectingStatus.Success));
            if (this._storeId) {
                this._socket.emit('store.join', this._storeId);
            }
        });

        this._socket.on('purchase.create', (purchase: IPurchase) => {
            _upsertPurchase(purchase);
        });

        this._socket.on(
            'purchase.updateStatus',
            async (purchase: IPurchase) => {
                _upsertPurchase(purchase);
            }
        );

        this._socket.on(
            'snackTV.restart',
            async (data?: { serial: string; }) => {
                if (data && (data.serial === serial || data.serial === deviceId)) {
                    // @ts-expect-error method is injected by react-native-webview
                    window.ReactNativeWebView.postMessage(JSON.stringify({
                        type: "Restart",
                        data: { extra: [], lvl: "", msg: "snackTV.restart" },
                    }));
                }
            }
        )

        this._socket.on('snackTV.updateScreen', async (screen: IScreen) => {
            _updateScreen(screen);
        });

        this._socket.on(
            'product.create',
            ({ storeId, product }: { storeId: string; product: IProduct }) =>
                _createProduct({ storeId, product })
        );

        this._socket.on(
            'product.update',
            async ({
                storeId,
                product,
            }: {
                storeId: string;
                product: IProduct;
            }) => _updateProduct({ storeId, product })
        );

        this._socket.on(
            'product.delete',
            async ({
                storeId,
                productId,
            }: {
                storeId: string;
                productId: string;
            }) => _deleteProduct({ storeId, productId })
        );

        this._socket.on('reconnect_attempt', async () => {
            !isProd &&
                console.log('[websockets.ts] reconnect_attempt:', {
                    uri,
                    query,
                });
            if (this.usingDeviceToken) {
                const freshToken = getDeviceToken();
                if (freshToken) {
                    this._socket.io.opts.query = { deviceToken: freshToken };
                }
            } else {
                const token = await this.getFirebaseToken();
                this._socket.io.opts.query = { token };
            }
        });

        this._socket.on('reconnect', async () => {
            !isProd &&
                console.log('[websockets.ts] reconnected:', { uri, query });
            this.connected = true;
            if (!this.shouldBackoffResync) {
                await this.resyncPurchases();
                await this.resyncProducts();
            }
            if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
            if (this.reconnectInterval) clearInterval(this.reconnectInterval);
            dispatch(setReconnecting(ReconnectingStatus.Success));
        });

        this._socket.on(
            'server.shutdown',
            async (data: {
                baseDurationSeconds: number;
                randomOffsetMaximumSeconds: number;
            }) => {
                this.shouldBackoffResync = true;
                const backoffAmountMS = Math.round(
                    (data.baseDurationSeconds +
                        Math.random() * data.randomOffsetMaximumSeconds) *
                        1000
                );

                if (this.afterReconnectTimeoutId) {
                    clearTimeout(this.afterReconnectTimeoutId);
                }

                this.afterReconnectTimeoutId = setTimeout(async () => {
                    this.resyncPurchases();
                    this.resyncProducts();
                    this.shouldBackoffResync = false;
                }, backoffAmountMS);
            }
        );

        this._socket.on('disconnect', async () => {
            this.connected = false;

            this.reconnectTimeout = setTimeout(async () => {
                // only set reconnecting status if user
                // is signed in/has an auth token, and
                // if the socket is still not connected after
                // the timeout expires
                const token = await this.getFirebaseToken();
                if (token && !this.connected) {
                    // set status to reconnecting, and
                    // continue to check if socket reconnected
                    dispatch(setReconnecting(ReconnectingStatus.Reconnecting));
                    this.reconnectInterval = setInterval(() => {
                        // this.connected case handled in socket.on reconnect
                        if (!this.connected) {
                            dispatch(
                                setReconnecting(ReconnectingStatus.Failed)
                            );
                        }
                    }, Constants.intervals.RECONNECT_INTERVAL);
                }
            }, Constants.intervals.RECONNECT_TIMEOUT);
        });
    }

    async resyncPurchases() {
        if (!this._storeId) {
            return;
        }
        try {
            _fetchPurchases({
                storeId: this._storeId,
                since: moment().clone().subtract(1.5, 'hours').toString(),
                count: 500,
            });
        } catch (err) {}
    }

    async resyncProducts() {
        if (!this._storeId) {
            return;
        }
        try {
            _fetchActiveStoreProducts(this._storeId);
        } catch (err) {}
    }

    disconnect() {
        if (this._socket) {
            this._socket.close();
        }
    }
}
